import { getFontCss, styleConstants } from '@paywithextend/components'
import { base_colorRaisedSurface } from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { Location } from 'history'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { connect, useDispatch, useSelector } from 'react-redux'
import { matchPath, Route, Switch } from 'react-router'
import Toast from 'src/app/Toast/Toast'
import FixedAlertBanner from 'src/components/AlertBars/FixedAlertBanner'
import CobrandLogo from 'src/components/CobrandLogo/CobrandLogo'
import DevToolsButton from 'src/components/DevToolsButton/DevToolsButton'
import SystemAlertPopup from 'src/components/SystemAlertPopup/SystemAlertPopup'
import { Environments } from 'src/configurations'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { unauthedAppRoute } from 'src/navigation/appRoutes'
import { connectedReplace, Rdrct } from 'src/navigation/Lnk'
import {
  receiptDeepLinkUrl,
  signinUrl,
  transactionAttachmentUrl,
} from 'src/navigation/urls'
import { createSetAuthedRedirectLocation } from 'src/redux/actions/auth.actions'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAlertMessageFromState,
  selectIsAlertBarOpenFromState,
  selectMessageCountFromState,
} from 'src/redux/selectors/app.selectors'
import { selectAlertBarPushdown } from 'src/redux/selectors/css.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const RedirectToSignin: React.FC<{
  location: Location
}> = ({ location }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      createSetAuthedRedirectLocation({
        ...location,
        state: undefined,
      }),
    )
    dispatch(connectedReplace(signinUrl()))
  }, [dispatch, location])

  return null
}

const Unauthed: React.FC = () => {
  const location = useSelector(selectLocationFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isAlertBarOpen = useSelector(selectIsAlertBarOpenFromState)
  const alertMessage = useSelector(selectAlertMessageFromState)
  const messageCount = useSelector(selectMessageCountFromState)
  const alertBarPushdown = useSelector(selectAlertBarPushdown)
  const appName = useSelector(selectAppName)
  const brandName = useSelector(selectBrandName)
  const env = useSelector(selectEnvironmentFromState)
  const translate = useTranslate()
  const isLegacyReceiptDeepLink = Boolean(
    matchPath(location.pathname, {
      exact: true,
      path: transactionAttachmentUrl(':id', ':attachmentId'),
    }),
  )
  const routeLocation = isLegacyReceiptDeepLink
    ? location
    : calculatedBackgroundLocation

  const [showSystemAlertPopup, setShowSystemAlertPopup] =
    useState<boolean>(false)

  return (
    <div
      css={css`
        min-height: 100vh;
        width: 100%;
        background-color: ${styleConstants.colors.gray_light};
      `}
    >
      {isAlertBarOpen && (
        <>
          <FixedAlertBanner
            variant={'soft'}
            title={translate('System alert')}
            message={alertMessage}
            messageCount={messageCount}
            link={
              <span
                onClick={(): void => setShowSystemAlertPopup(true)}
                onKeyPress={(): void => setShowSystemAlertPopup(true)}
                role="button"
                tabIndex={0}
                aria-label={'view-status-button'}
                css={css`
                  cursor: pointer;
                `}
              >
                {translate('View status')}
              </span>
            }
            top={alertBarPushdown}
            data-testid={'system-alert-banner'}
          />
          {showSystemAlertPopup && (
            <SystemAlertPopup
              onClose={(): void => setShowSystemAlertPopup(false)}
            />
          )}
        </>
      )}
      {(brandName === SupportedBrands.Pnc ||
        brandName === SupportedBrands.Bmo ||
        brandName === SupportedBrands.Jpm) && (
        <div
          css={css`
            margin-left: 30px;
            ${brandName === SupportedBrands.Jpm &&
            css`
              margin-left: 48px;
              margin-top: 48px;
            `}
            display: flex;
            flex-direction: row;
            align-items: center;
            position: fixed;
            z-index: 10;
          `}
        >
          {brandName === SupportedBrands.Pnc ? (
            <div
              css={css`
                background-color: ${base_colorRaisedSurface};
                border-bottom-left-radius: ${spacing(1)};
                border-bottom-right-radius: ${spacing(1)};
                box-shadow: 0 3px 4px rgba(0, 0, 0, 0.1);
                padding: ${spacing(4)} ${spacing(3)} ${spacing(3)};
                line-height: 0;
                img {
                  display: block;
                  width: 77px;
                  height: 12px;
                }
              `}
            >
              <CobrandLogo brandName={brandName} />
            </div>
          ) : (
            <CobrandLogo brandName={brandName} />
          )}
          {brandName !== SupportedBrands.Jpm && (
            <div
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-top: 10px;
                margin-left: 10px;
              `}
            >
              {translate('In collaboration with Extend')}
            </div>
          )}
        </div>
      )}
      <Helmet>
        <title>{`${appName}`}</title>
        <meta
          name="description"
          content={`Log into ${appName} to create, send, and manage virtual cards for all your business payments.`}
        />
      </Helmet>
      <Switch location={routeLocation}>
        {unauthedAppRoute.subRoutes.map((route, i) => (
          <Route key={i} {...route.routeProps}>
            {route.component}
          </Route>
        ))}
        <Route
          exact
          path={[
            receiptDeepLinkUrl(':id'),
            transactionAttachmentUrl(':id', ':attachmentId'),
          ]}
        >
          <RedirectToSignin location={location} />
        </Route>
        <Route path={'/'}>
          <Rdrct to={signinUrl()} />
        </Route>
      </Switch>
      {env !== Environments.Prod && (
        <div
          css={css`
            position: fixed;
            bottom: 5px;
            right: 5px;
            transform: scale(0.4);
            transform-origin: right bottom;
          `}
        >
          <DevToolsButton />
        </div>
      )}
      <Toast />
    </div>
  )
}

// TODO: this is NOT NECESSARY but for some reason useSelector returns a stale value,
// even when checking the values in the equality comparison and ensuring that a === b is false
export default connect(
  (state: RootState) => ({
    bgl: selectCalculatedBackgroundLocation(state),
  }),
  {},
)(Unauthed)
